import type { RefObject } from "react";
import { useEffect } from "react";

/**
 * Keeps a Mapbox map's drawing the same size as its container.
 *
 * Mapbox only resizes itself on a window `resize` event, but the full-bleed
 * map's container also changes width when the page's vertical scrollbar comes
 * or goes (e.g. when the quote list loads), with no window resize.
 */
export function useMapsResizeWithContainer(
  map: Pick<mapboxgl.Map, "resize"> | undefined,
  containerRef: RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    const container = containerRef.current;
    if (map === undefined || !container) {
      return;
    }
    const mapToResize = map;

    function resize() {
      mapToResize.resize();
    }

    const observer = new ResizeObserver(resize);
    observer.observe(container);

    return function () {
      observer.disconnect();
    };
  }, [map, containerRef]);
}
